/**
 * Per-tab session identifier for the `X-Device-Session-Id` request header
 * (DEV-11834).
 *
 * Requirements:
 *   - One value for the lifetime of a browser tab/window.
 *   - New value when the user opens a new tab or reopens after closing.
 *   - Available before the user is authenticated.
 *   - Resolves synchronously so request interceptors can run on the hot path.
 *
 * Strategy: random UUID generated on first request in the tab, persisted to
 * `sessionStorage`. Tied to the tab/window lifecycle by the browser — no
 * manual rotation needed.
 *
 *   1. Module-level in-memory cache (zero work after the first call).
 *   2. `sessionStorage` (survives in-tab reloads, navigations and Tauri
 *      webview reloads; cleared by the browser on tab close).
 *   3. Generate a fresh UUID when storage is empty or unreachable.
 *
 * Caveats:
 *   - In a Tauri desktop build, the webview's sessionStorage typically
 *     persists for the lifetime of the app process. This matches the
 *     "session = app run" semantics agreed in the DEV-11834 thread, pending
 *     final confirmation with Jonathan He.
 *   - On Safari ITP / locked-down profiles where sessionStorage is blocked,
 *     the in-memory cache still keeps the value stable within a tab.
 */

const STORAGE_KEY = 'heidi_device_session_id';
const FALLBACK = 'unknown';

let memoryCache: string | null = null;

function safeSessionStorageGet(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function safeSessionStorageSet(value: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // sessionStorage may be blocked (privacy mode, quota, ITP).
    // The in-memory cache is still populated for the tab lifetime.
  }
}

function generateSessionId(): string {
  if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') {
    return FALLBACK;
  }
  try {
    return crypto.randomUUID();
  } catch {
    return FALLBACK;
  }
}

/**
 * Returns the device session id synchronously. Safe to call from axios
 * request interceptors. Never throws.
 */
export function getDeviceSessionId(): string {
  if (memoryCache) return memoryCache;

  const stored = safeSessionStorageGet();
  if (stored) {
    memoryCache = stored;
    return stored;
  }

  const generated = generateSessionId();
  memoryCache = generated;
  if (generated !== FALLBACK) {
    safeSessionStorageSet(generated);
  }
  return generated;
}

/** @internal — for tests only. Resets the module-level cache. */
export function __resetDeviceSessionIdCacheForTesting(): void {
  memoryCache = null;
}
